import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Table,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Tooltip,
  Dropdown,
  Modal,
  message,
  Form,
  Select,
  Switch,
  type MenuProps,
} from 'antd';
import {
  ReloadOutlined,
  CodeOutlined,
  PlusOutlined,
  RightOutlined,
  MoreOutlined,
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  PushpinOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import type { PrebuiltPrompt, PrebuiltPromptType } from '@/api/types';
import { typeFromValue } from '@/api/prebuiltPrompts';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { SectionCard } from '@/components/SectionCard';
import { PromptAgentLinkageDrawer } from './PromptAgentLinkageDrawer';
import { PromptReviewDrawer } from './PromptReviewDrawer';
import { PrebuiltPromptHistoryDrawer } from './PrebuiltPromptHistoryDrawer';
import { AgentPickerTrigger } from '@/components/AgentPickerDrawer';

const { Text, Paragraph } = Typography;

const TYPE_TONE: Record<PrebuiltPromptType, PillTone> = {
  QUICK_START: 'ink',
  AGENT_INSTRUCTION: 'violet',
};

const TYPE_LABEL: Record<PrebuiltPromptType, string> = {
  QUICK_START: 'Quick start',
  AGENT_INSTRUCTION: 'Agent instruction',
};

// Library of "starter prompts" — the chip strip in agent chat ("Brief me on
// this patient", "What needs attention?", …). Authoring lives here so each
// merchant can curate its own quick-actions per app surface.

export const PrebuiltPromptsPage = observer(function PrebuiltPromptsPage() {
  const { prebuiltPromptStore: store, merchantContextStore: ctx, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PrebuiltPrompt | null>(null);
  const [linkagePromptId, setLinkagePromptId] = useState<PrebuiltPrompt | null>(null);
  const [reviewing, setReviewing] = useState<PrebuiltPrompt | null>(null);
  // History drawer — reads ai.PrebuiltPromptHistory via the new
  // /api/PrebuiltPrompt/History/{promptId} endpoint. Surfaced as a row
  // menu item so operators can audit body changes (manual or AI-driven)
  // without leaving the list page.
  const [historyPrompt, setHistoryPrompt] = useState<PrebuiltPrompt | null>(null);

  // Re-fetch on every page mount. Without this, the page relies on the
  // store's reaction-on-merchantId-change, which doesn't re-fire when the
  // user navigates away and back. Same post-deploy stale-state failure
  // mode that AgentsListPage had — see that file's comment for the full
  // narrative.
  useEffect(() => {
    if (ctx.selectedMerchantId != null) {
      void store.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (ctx.selectedMerchantId == null) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<CodeOutlined />}
          title="Pick a merchant to begin"
          description="Use the merchant switcher in the top bar to scope the prompt library."
          size="lg"
        />
      </SectionCard>
    );
  }

  function openEdit(row: PrebuiltPrompt | null): void {
    setEditing(row);
    setEditorOpen(true);
    trackEvent('Prompt.EditOpened', { id: row?.id ?? 'new' });
  }

  function moreMenu(row: PrebuiltPrompt): MenuProps {
    return {
      items: [
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: 'Edit',
          onClick: () => openEdit(row),
        },
        {
          key: 'review',
          icon: <RobotOutlined />,
          label: 'Review with AI',
          onClick: () => {
            setReviewing(row);
            trackEvent('Prompt.ReviewWithAI', { id: row.id });
          },
        },
        {
          key: 'history',
          icon: <HistoryOutlined />,
          label: 'Version history',
          onClick: () => {
            setHistoryPrompt(row);
            trackEvent('Prompt.HistoryOpened', { id: row.id });
          },
        },
        {
          key: 'pinned',
          icon: <PushpinOutlined />,
          label: 'Manage pinned agents',
          onClick: () => setLinkagePromptId(row),
        },
        {
          key: 'copy',
          icon: <CopyOutlined />,
          label: `Copy id #${row.id}`,
          onClick: () => {
            void navigator.clipboard?.writeText(String(row.id));
            message.success('Id copied');
          },
        },
        { type: 'divider' as const },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: 'Delete prompt',
          danger: true,
          onClick: () => {
            Modal.confirm({
              title: 'Delete this prompt?',
              content: `"${row.description ?? row.name ?? `#${row.id}`}" will be removed.`,
              okText: 'Delete',
              okButtonProps: { danger: true },
              onOk: async () => {
                try {
                  await store.remove(row.id);
                  message.success('Prompt deleted');
                  trackEvent('Prompt.Deleted', { id: row.id });
                } catch (err) {
                  message.error(err instanceof Error ? err.message : 'Delete failed');
                }
              },
            });
          },
        },
      ],
    };
  }

  const columns: ColumnsType<PrebuiltPrompt> = [
    {
      title: 'Description',
      key: 'description',
      sorter: (a, b) => (a.description ?? '').localeCompare(b.description ?? ''),
      render: (_text, row) => (
        <div>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: isDark ? colors.slate[50] : colors.slate[900],
              letterSpacing: '-0.005em',
            }}
          >
            {row.description ?? <span style={{ fontStyle: 'italic' }}>(no description)</span>}
          </div>
          {(row.buttonText || row.name) && (
            <div
              style={{
                fontSize: 11.5,
                color: isDark ? colors.slate[400] : colors.slate[500],
                fontFamily: "'JetBrains Mono', monospace",
                marginTop: 3,
              }}
            >
              {row.buttonText ? `“${row.buttonText}”` : row.name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 160,
      filters: [
        { text: 'Quick start', value: 'QUICK_START' },
        { text: 'Agent instruction', value: 'AGENT_INSTRUCTION' },
      ],
      onFilter: (val, row) => typeFromValue(row.type) === val,
      render: (_text, row) => {
        const t = typeFromValue(row.type);
        return <StatusPill tone={TYPE_TONE[t]}>{TYPE_LABEL[t]}</StatusPill>;
      },
    },
    {
      title: 'Surface',
      key: 'surface',
      width: 170,
      render: (_text, row) => {
        const tags: React.ReactNode[] = [];
        if (row.memberApp) tags.push(<StatusPill key="m" tone="ink">Patient app</StatusPill>);
        if (row.merchantApp)
          tags.push(<StatusPill key="c" tone="violet">Care team</StatusPill>);
        if (!row.memberApp && !row.merchantApp)
          tags.push(<StatusPill key="n" tone="slate">Hidden</StatusPill>);
        return <Space size={4}>{tags}</Space>;
      },
    },
    {
      title: (
        <Tooltip title="Click any cell to manage which agents pin this prompt as a chat chip.">
          <span>
            <PushpinOutlined style={{ marginRight: 5 }} />
            Pinned agents
          </span>
        </Tooltip>
      ),
      key: 'pinnedAgents',
      width: 170,
      sorter: (a, b) => store.agentCountFor(a.id) - store.agentCountFor(b.id),
      onCell: (row) => ({
        onClick: (e) => {
          e.stopPropagation();
          setLinkagePromptId(row);
          trackEvent('Prompt.LinkageDrawerOpened', { id: row.id });
        },
        style: { cursor: 'pointer' },
      }),
      render: (_text, row) => {
        const count = store.agentCountFor(row.id);
        const names = store.agentNamesFor(row.id);
        if (count === 0) {
          return (
            <Tooltip title="Click to pin this prompt to one or more agents.">
              <span>
                <StatusPill tone="slate" icon={<PushpinOutlined />}>
                  Not pinned
                </StatusPill>
              </span>
            </Tooltip>
          );
        }
        return (
          <Tooltip
            title={
              <div style={{ maxWidth: 240 }}>
                {names.slice(0, 8).map((n, i) => (
                  <div key={i} style={{ fontSize: 12 }}>
                    • {n}
                  </div>
                ))}
                {names.length > 8 && (
                  <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                    +{names.length - 8} more
                  </div>
                )}
              </div>
            }
          >
            <span>
              <StatusPill tone="ink" icon={<PushpinOutlined />}>
                {count} {count === 1 ? 'agent' : 'agents'}
              </StatusPill>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Category',
      key: 'category',
      width: 140,
      render: (_text, row) =>
        row.category ? (
          <StatusPill tone="slate">{row.category.title ?? row.category.name ?? `#${row.category.id}`}</StatusPill>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (v: number) => (
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            color: isDark ? colors.slate[400] : colors.slate[500],
          }}
        >
          #{v}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      fixed: 'right',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_text, row) => (
        <Space size={2}>
          <Tooltip title="Edit prompt">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            >
              Edit
            </Button>
          </Tooltip>
          <Dropdown menu={moreMenu(row)} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} aria-label="More actions" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="ink"
        icon={<ThunderboltOutlined />}
        title="Prebuilt prompts"
        helpTopic="prompts.prebuilt"
        subtitle={`Starter prompts for ${ctx.selectedMerchant?.title ?? 'this merchant'} — surfaced as quick-action chips in chat and as canned agent instructions.`}
        count={store.totalCount}
        countLabel={store.totalCount === 1 ? 'prompt' : 'prompts'}
        actions={
          <Space>
            <Input.Search
              placeholder="Search prompts…"
              allowClear
              style={{ width: 280 }}
              value={store.search}
              onChange={(e) => store.setSearch(e.target.value)}
              onSearch={() => void store.fetch()}
            />
            <Select
              allowClear
              placeholder="Type"
              style={{ width: 180 }}
              value={store.typeFilter ?? undefined}
              onChange={(v) => {
                store.setTypeFilter((v as PrebuiltPromptType) ?? null);
                void store.fetch();
              }}
              options={[
                { value: 'QUICK_START', label: 'Quick start' },
                { value: 'AGENT_INSTRUCTION', label: 'Agent instruction' },
              ]}
            />
            <AgentPickerTrigger
              value={store.agentFilterId}
              onChange={(id) => store.setAgentFilterId(id)}
              placeholder="Pinned to agent"
              drawerTitle="Filter by pinned agent"
              clearLabel="Any agent"
              style={{ minWidth: 220 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void store.fetch()}
              loading={store.isLoading}
            >
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>
              New prompt
            </Button>
          </Space>
        }
      />

      {store.error && (
        <Alert
          type="error"
          message="Failed to load prompts"
          description={store.error}
          showIcon
          closable
        />
      )}

      <SectionCard noPadding>
        <Table<PrebuiltPrompt>
          rowKey="id"
          columns={columns}
          dataSource={store.items}
          loading={store.isLoading}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            showTotal: (t) => `${t} prompts`,
            style: { padding: '12px 18px', margin: 0 },
          }}
          onRow={(row) => ({
            onClick: () => openEdit(row),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: (
              <BrandedEmpty
                icon={<RightOutlined />}
                title={store.search ? 'No prompts match' : 'No starter prompts yet'}
                description={
                  store.search
                    ? 'Try clearing the search or type filter.'
                    : 'Create a prompt that becomes a one-click chip in agent chat.'
                }
                action={
                  !store.search ? (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>
                      Create prompt
                    </Button>
                  ) : undefined
                }
              />
            ),
          }}
        />
      </SectionCard>

      <PromptEditor
        open={editorOpen}
        prompt={editing}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
      />

      <PromptAgentLinkageDrawer
        open={linkagePromptId != null}
        prompt={linkagePromptId}
        onClose={() => setLinkagePromptId(null)}
      />

      <PromptReviewDrawer
        open={reviewing != null}
        promptKind="Prebuilt"
        promptId={reviewing?.id ?? 0}
        promptLabel={reviewing?.description ?? reviewing?.name ?? null}
        onClose={() => setReviewing(null)}
        onApplied={() => void store.fetch()}
      />

      {/* Audit trail — every body change to ai.PrebuiltPrompt.Instructions
          appears here, tagged with its source (manual edit / AI accept /
          AI edit / import) and the editor name. */}
      <PrebuiltPromptHistoryDrawer
        open={historyPrompt != null}
        promptId={historyPrompt?.id ?? null}
        promptLabel={historyPrompt?.description ?? historyPrompt?.name ?? null}
        onClose={() => setHistoryPrompt(null)}
      />
    </div>
  );
});

// ── Editor modal (create + edit) ────────────────────────────────────────────

interface PromptEditorProps {
  open: boolean;
  prompt: PrebuiltPrompt | null;
  onClose: () => void;
}

const PromptEditor = observer(function PromptEditor({ open, prompt, onClose }: PromptEditorProps) {
  const { prebuiltPromptStore: store } = useStore();
  const [form] = Form.useForm<{
    type: PrebuiltPromptType;
    description: string;
    name?: string;
    buttonText?: string;
    instructions?: string;
    help?: string;
    memberApp: boolean;
    merchantApp: boolean;
  }>();
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!prompt;

  const initial = prompt
    ? {
        type: typeFromValue(prompt.type),
        description: prompt.description ?? '',
        name: prompt.name ?? undefined,
        buttonText: prompt.buttonText ?? undefined,
        instructions: prompt.instructions ?? undefined,
        help: prompt.help ?? undefined,
        memberApp: prompt.memberApp ?? false,
        merchantApp: prompt.merchantApp ?? true,
      }
    : {
        type: 'QUICK_START' as PrebuiltPromptType,
        description: '',
        memberApp: false,
        merchantApp: true,
      };

  async function handleSubmit(): Promise<void> {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload: Partial<PrebuiltPrompt> = {
        ...(isEdit ? { id: prompt!.id } : {}),
        type: values.type === 'QUICK_START' ? 0 : 1,
        description: values.description.trim(),
        name: values.name?.trim() || undefined,
        buttonText: values.buttonText?.trim() || undefined,
        instructions: values.instructions?.trim() || undefined,
        help: values.help?.trim() || undefined,
        memberApp: values.memberApp,
        merchantApp: values.merchantApp,
      };
      if (isEdit) {
        await store.update(payload);
        message.success('Prompt updated');
      } else {
        await store.create(payload);
        message.success('Prompt created');
      }
      form.resetFields();
      onClose();
    } catch (err: unknown) {
      const isFormError = err && typeof err === 'object' && 'errorFields' in err;
      if (!isFormError) {
        message.error(err instanceof Error ? err.message : 'Failed to save');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={handleSubmit}
      okText={isEdit ? 'Save changes' : 'Create prompt'}
      okButtonProps={{ loading: submitting }}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ThunderboltOutlined /> {isEdit ? 'Edit prompt' : 'New prompt'}
        </span>
      }
      destroyOnClose
      width={680}
    >
      <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 14 }}>
        Quick-start prompts surface as one-click chips above the chat box. Agent-instruction
        prompts can be pinned to an agent and pre-fill the operator's "what should this agent do
        next?" workflow.
      </Paragraph>
      <Form form={form} layout="vertical" preserve={false} initialValues={initial}>
        <Form.Item name="type" label="Type" rules={[{ required: true }]}>
          <Select
            options={[
              {
                value: 'QUICK_START',
                label: (
                  <span>
                    <ThunderboltOutlined /> Quick start (chat chip)
                  </span>
                ),
              },
              {
                value: 'AGENT_INSTRUCTION',
                label: (
                  <span>
                    <RobotOutlined /> Agent instruction (operator pin)
                  </span>
                ),
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          extra="What this prompt does — shown in the table and used as fallback chip text."
          rules={[{ required: true, message: 'A description is required' }]}
        >
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>
        <Form.Item
          name="buttonText"
          label="Chip / button text"
          extra="Short label users see on the chip. Leave blank to use Description."
        >
          <Input maxLength={80} placeholder='e.g. "Brief me on this patient"' />
        </Form.Item>
        <Form.Item name="name" label="Internal name (optional)">
          <Input maxLength={80} placeholder="patient.brief" />
        </Form.Item>
        <Form.Item
          name="instructions"
          label="Instructions"
          extra="The literal text that gets injected when the user clicks this chip."
        >
          <Input.TextArea
            rows={5}
            placeholder="Tell the user what's overdue, what's blocking, and what's next."
          />
        </Form.Item>
        <Form.Item name="help" label="Help text" extra="Shown when the user hovers the chip.">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item label="Surfaces" required>
          <Space size={20}>
            <Form.Item name="memberApp" valuePropName="checked" noStyle>
              <Switch checkedChildren="Patient" unCheckedChildren="Patient" />
            </Form.Item>
            <Form.Item name="merchantApp" valuePropName="checked" noStyle>
              <Switch checkedChildren="Care team" unCheckedChildren="Care team" />
            </Form.Item>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
});
